import stearsLogoBlack from "@/public/assets/img/stears-logo-black.png"
import stearsLogoWhite from "@/public/assets/img/stears-logo-white.png"
import { ARTICLE_PATH_MATCHES } from "@/src/common/business-layer/constants"
import { Link } from "@/src/common/components/Link"
import useTheme from "@/src/common/hooks/useTheme"
import { parseSearchParam } from "@/src/common/utils"
import { useGlobalSearch } from "@/src/contexts/GlobalSearchContext"
import {
  LayoutPropsV3,
  SidebarMenuItem,
  SidebarMenuItemV2
} from "@/src/sb/business-layer/types"
import { useAnalytics } from "@/src/sb/components/Analytics"
import { SearchInput } from "@/src/sb/components/Input"
import SearchInputV2 from "@/src/sb/components/Input/SearchInputV2"
import { IntelligenceSolutionsBanner } from "@/src/sb/components/IntelligenceSolutionsBanner"
import { Accounts } from "@/src/sb/components/Navigationbar/Accounts"
import { Icon } from "@/src/sb/components/Navigationbar/MobileNavbar/Icon"
import MobileNavbarV2 from "@/src/sb/components/Navigationbar/MobileNavbar/MobileNavbarV2"
import PlatformisationMobileNavbar from "@/src/sb/components/Navigationbar/MobileNavbar/PlatformisationMobileNavbar"
import Sidebar from "@/src/sb/components/Navigationbar/Sidebar"
import SidebarV2 from "@/src/sb/components/Navigationbar/SidebarV2"
import { SEO } from "@/src/sb/components/SEO"
import { ProfileUpdateModal } from "@/src/sb/components/auth/ProfileUpdate"
import Spinner from "@/src/sb/components/icon/Spinner"
import StearsLogo from "@/src/sb/components/icon/StearsLogo"
import { HIDE_INTELLIGENCE_SOLUTIONS_BANNER } from "@/src/sb/constants"
import { MenuItemSeparator, accounts } from "@/src/sb/constants/copy/navItems"
import PageLinks, { pages } from "@/src/sb/constants/copy/pageLinks"
import {
  ENABLE_SEARCH_V2,
  EXTRA_SIDEBAR_ITEMS,
  EXTRA_SIDEBAR_TOP_ITEMS,
  USE_V2_TAG_CLASSIFICATION
} from "@/src/sb/constants/featureFlags"
import { showIntelligenceBannerOnPath } from "@/src/sb/core/utils/helpers"
import { useUser } from "@/src/sb/hooks/useUser"
import { useWindowSize } from "@/src/sb/hooks/useWindowSize"
import { useFeatureValue } from "@growthbook/growthbook-react"
import classNames from "classnames"
import { isEmpty, isEqual, noop, omitBy } from "lodash"
import Image from "next/image"
import { useRouter } from "next/router"
import React, { ReactNode, useEffect, useState } from "react"

export const HIDDEN_MOBILE_MENU_NAVBAR_CLASS = ["private equity", "open data"]

export const StearsLogoNode = ({
  scale = 0.5,
  useSVG = false,
  width,
  height
}: {
  scale?: number
  useSVG?: boolean
  width?: number
  height?: number
}) => {
  const analytics = useAnalytics()

  return (
    <Link
      href={pages.home}
      onClick={() => analytics?.clickNavItem("logo")}
      className={
        "inline-block !text-stears_black dark:!text-stears_white leading-none"
      }
    >
      {useSVG ? (
        <StearsLogo scale={scale} />
      ) : (
        <>
          <Image
            src={stearsLogoBlack}
            width={width}
            height={height}
            alt={"Stears"}
            className={"dark:hidden"}
          />
          <Image
            src={stearsLogoWhite}
            width={width}
            height={height}
            alt={"Stears"}
            className={"hidden dark:block"}
          />
        </>
      )}
    </Link>
  )
}

const DefaultLayoutV3 = ({
  children,
  seo,
  showNavBar = true,
  topBarAddon = null,
  navigationMenu = [],
  onScroll,
  setNextQueryParam,
  searchInputProps,
  onFilterToggle,
  features
}: { children: ReactNode } & LayoutPropsV3) => {
  const updateNavigationMenu = {
    ...(features?.[EXTRA_SIDEBAR_TOP_ITEMS] as Record<string, any>),
    ...navigationMenu,
    ...(features?.[EXTRA_SIDEBAR_ITEMS] as Record<string, any>)
  } as Record<string, SidebarMenuItemV2[]>
  const user = useUser()
  const menuItems = accounts[user.userType]

  const router = useRouter()
  const { asPath } = router
  const analytics = useAnalytics()
  const [showIntelligenceSolutionsBanner, setShowIntelligenceSolutionsBanner] =
    useState(false)
  const [showAccountMenu, setShowAccountMenu] = useState(false)
  const { openSearch } = useGlobalSearch()

  const [isMobile] = useWindowSize()
  const [showSideBar, setShowSideBar] = useState(false)
  const [sidebarRootItemsToHide, setSidebarRootItemsToHide] = useState<
    Array<number>
  >([])
  const [accountName, setAccountName] = useState("Account")
  const { theme } = useTheme()
  const [searchQuery, setSearchQuery] = useState("")
  const [makeInputReadOnly, setMakeInputReadOnly] = useState(false)
  const [showStearsLogo, setShowStearsLogo] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const useV2TagClassification = useFeatureValue(
    USE_V2_TAG_CLASSIFICATION,
    features?.[USE_V2_TAG_CLASSIFICATION] ?? false
  )

  const useSearchV2 = useFeatureValue(
    ENABLE_SEARCH_V2,
    features?.[ENABLE_SEARCH_V2] ?? false
  )

  const tags = router.query.tags
    ? typeof router.query.tags === "string"
      ? [router.query.tags]
      : router.query.tags
    : []

  useEffect(() => {
    setSearchQuery((router.query.q as string) ?? "")
  }, [router.query.q])

  useEffect(() => {
    let accountName = user.data?.firstName || "Account"

    if (
      router.isReady &&
      ARTICLE_PATH_MATCHES.some((path) => path.test(router.asPath))
    )
      accountName = "Account"

    setAccountName(accountName)
  }, [router.isReady])

  useEffect(() => {
    setShowIntelligenceSolutionsBanner(
      !localStorage.getItem(HIDE_INTELLIGENCE_SOLUTIONS_BANNER)
    )
  }, [])

  useEffect(() => {
    if (isMobile !== undefined) {
      setIsLoading(false)
    }
  }, [isMobile])

  const extraSearchAction = router.asPath.startsWith(PageLinks.search) ? (
    <button
      className={
        "flex justify-center h-full items-center bg-gray-100 dark:bg-[#4B5563] dark:text-white p-2.5 dark:-mr-2.5 w-8 md:w-10 lg:hidden"
      }
      onClick={onFilterToggle}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.125 11.5H5.375C4.89175 11.5 4.5 11.1082 4.5 10.625V7.30437L1.25812 4.0625C1.09381 3.89916 1.00098 3.67731 1 3.44563V1.875C1 1.39175 1.39175 1 1.875 1H10.625C11.1082 1 11.5 1.39175 11.5 1.875V3.44563C11.499 3.67731 11.4062 3.89916 11.2419 4.0625L8 7.30437V10.625C8 11.1082 7.60825 11.5 7.125 11.5ZM1.875 1.87363V3.44426L5.375 6.94426V10.6236H7.125V6.94426L10.625 3.44426V1.87363H1.875Z"
          fill="currentColor"
        />
      </svg>
    </button>
  ) : null

  const toggleMobileNavbar = (rootItemsToHide: Array<number> = []) => {
    const _showSidebar = !showSideBar
    setShowSideBar(_showSidebar)
    const newNavItems = Object?.keys(updateNavigationMenu) || []
    const listToHide = newNavItems?.map((item, idx) => {
      if (Array.isArray(rootItemsToHide) && rootItemsToHide.includes(idx)) {
        return item
      }
      return null
    })

    setShowAccountMenu(_showSidebar ? !listToHide.includes("open data") : false)

    setSidebarRootItemsToHide(showSideBar ? [] : rootItemsToHide)
  }

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    searchInputProps?.onChange?.(e)
  }

  const handleSearch = async (searchString?: string) => {
    const sQ = (searchString ?? searchQuery).trim()
    const rQ = (router.query.q as string)?.trim() ?? ""
    const hasPendingTags = Boolean(searchInputProps?.tags)

    if (
      (!sQ && !rQ && !hasPendingTags) ||
      (sQ === rQ && isEqual(searchInputProps?.tags ?? [], tags))
    )
      return

    setMakeInputReadOnly(true)
    analytics.searchForArticle(sQ)
    await router.push(
      sQ.trim() || tags
        ? {
            pathname: PageLinks.search,
            query: omitBy(
              // FYI:
              // Recommend the query property order remains consistent for performance improvement
              {
                q: sQ.trim(),
                tags: searchInputProps?.tags ?? tags
              },
              isEmpty
            )
          }
        : { pathname: PageLinks.search }
    )

    setMakeInputReadOnly(false)
  }

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      return
    }
    searchInputProps?.onKeyDown?.(e)
  }

  if (isLoading) {
    return (
      <div className={theme}>
        <aside
          className={`w-full md:w-[256px] fixed left-0 top-0 h-screen dark:bg-[#0E0F10]`}
        />
      </div>
    )
  }

  const hideMobileNavbar = router.asPath.includes("open-data")
  const moreAddonItem = menuItems.map((item) => {
    if ("type" in item && item.type === "separator") {
      return item
    }
    const linkItem = item as Exclude<typeof item, MenuItemSeparator>
    return {
      ...linkItem,
      href:
        setNextQueryParam && linkItem?.canHaveNextQueryParam
          ? `${linkItem?.href}?${parseSearchParam({ next: asPath }).toString()}`
          : linkItem?.href
    }
  })

  const isLicenceHolder = user?.userType === "subscriber"

  const openSearchPatched = isLicenceHolder
    ? () => {
        openSearch()
        analytics.startGlobalSearch()
      }
    : noop

  const shouldDisableSearch = !isLicenceHolder

  return (
    <>
      <SEO {...seo} />
      <ProfileUpdateModal />

      <div className={theme}>
        <section
          className={classNames(
            "md:pt-[80px] h-screen overflow-hidden dark:bg-[#1A1D21] dark:text-stears_white",
            {
              "ml-[256px]": !isMobile,
              // Having this class "pt-[Xpx]" only applied
              // when the sidebar is not open is
              // to fix an issue in safari
              // when the sideBar logo gets covered
              // due to the presence of the padding top
              "pt-[60px] md:pt-[80px]": isMobile && !showSideBar,
              "pb-[67px]": !hideMobileNavbar && isMobile && showNavBar
            }
          )}
        >
          {!isMobile || showSideBar ? (
            <aside
              className={classNames(
                "w-full md:w-[256px] fixed left-0 top-0 h-screen bg-white dark:bg-[#0E0F10] dark:text-stears_white border-r-[0.5px] dark:border-gray-600",
                {
                  "z-20": isMobile
                }
              )}
            >
              {useV2TagClassification ? (
                <SidebarV2
                  features={features}
                  sidebarMenuItems={
                    updateNavigationMenu as Record<string, SidebarMenuItemV2[]>
                  }
                  sidebarRootItemsToHide={sidebarRootItemsToHide}
                  onClose={toggleMobileNavbar}
                  asideFooter={
                    showAccountMenu && isMobile ? (
                      <ul
                        className={
                          "py-2.5 border-t-[0.5px] border-[#4B5563] -mx-5 text-[#9CA3AF]"
                        }
                      >
                        {moreAddonItem.map((item, index) => {
                          if ("type" in item && item.type === "separator") {
                            return (
                              <li
                                key={index}
                                className="h-[1px] my-2.5 bg-[#4B5563] -mx-5"
                              />
                            )
                          }
                          const linkItem = item as Exclude<
                            typeof item,
                            MenuItemSeparator
                          >
                          return (
                            <li key={linkItem.label}>
                              <Link
                                href={linkItem.href}
                                onClick={() => {
                                  linkItem?.handleClick?.(analytics, {
                                    context: "mobile_navbar"
                                  })
                                }}
                                className={
                                  "flex px-5 py-[6px] gap-2.5 items-center font-medium text-sm dark:!text-stears_white"
                                }
                              >
                                <span className={"shrink-0"}>
                                  {linkItem.icon ? (
                                    typeof linkItem.icon === "string" ? (
                                      <Icon
                                        src={linkItem.icon}
                                        alt={linkItem.label}
                                      />
                                    ) : (
                                      linkItem.icon
                                    )
                                  ) : null}
                                </span>{" "}
                                <span className={"grow"}>{linkItem.label}</span>
                              </Link>
                            </li>
                          )
                        })}
                      </ul>
                    ) : null
                  }
                />
              ) : (
                <Sidebar
                  sidebarMenuItems={navigationMenu as SidebarMenuItem[]}
                  sideBarRootItemsToHide={sidebarRootItemsToHide}
                  onClose={toggleMobileNavbar}
                />
              )}
            </aside>
          ) : null}

          {showNavBar ? (
            <nav>
              {!(showSideBar && isMobile) ? (
                <section
                  className={"fixed top-0 left-0 md:left-[256px] right-0 z-50"}
                >
                  <section
                    className={
                      "dark:bg-[#0E0F10] dark:text-stears_white border-b-[0.5px] dark:border-gray-600 h-[60px] md:h-[80px]"
                    }
                  >
                    <section
                      className={
                        "px-2.5 py-[14px] md:p-5 grow flex items-center gap-6"
                      }
                    >
                      {showStearsLogo ? (
                        <section className={"md:hidden flex items-center"}>
                          <StearsLogoNode useSVG height={21.3} />
                        </section>
                      ) : null}

                      <section className={"grow"}>
                        <section className="w-full md:max-w-[600px] flex justify-end">
                          {useSearchV2 ? (
                            <SearchInputV2
                              className={classNames(
                                "md:border-[0.5px] md:border-[#4B5563] md:dark:border-[0.5px] md:bg-[#1F2937] cursor-pointer",
                                { "!cursor-not-allowed": shouldDisableSearch }
                              )}
                              placeholder="Search for companies, transactions and more"
                              onClick={openSearchPatched}
                              collapsed={Boolean(isMobile)}
                              cursorBehavior={
                                shouldDisableSearch ? "not-allowed" : "pointer"
                              }
                              onFilterToggle={onFilterToggle}
                              onClear={() => {
                                setSearchQuery("")
                                handleSearch("")
                                searchInputProps?.onClear?.()
                              }}
                              value={searchQuery}
                              readOnly
                            />
                          ) : (
                            <SearchInput
                              {...searchInputProps}
                              className={classNames(
                                searchInputProps?.className,
                                { "!cursor-not-allowed": shouldDisableSearch }
                              )}
                              placeholder="Search for datasets and reports on countries, industries, themes, and companies."
                              ariaLabel="Search"
                              collapsed={Boolean(isMobile)}
                              name="q"
                              value={searchQuery}
                              autoComplete={"off"}
                              autoFocus={true}
                              readOnly={
                                makeInputReadOnly || shouldDisableSearch
                              }
                              onChange={handleSearchInputChange}
                              onSearchInputClose={() => setShowStearsLogo(true)}
                              onSearchInputOpen={() => setShowStearsLogo(false)}
                              onKeyDown={handleKeyDown}
                              onClear={() => {
                                setSearchQuery("")
                                handleSearch("")
                                searchInputProps?.onClear?.()
                                analytics.clearArticleSearch(searchQuery)
                              }}
                              extraActionNode={extraSearchAction}
                            />
                          )}
                        </section>
                      </section>

                      <section className={"-md:hidden"}>
                        {user.loading && <Spinner />}

                        {!user.loading && (
                          <Accounts
                            name={accountName}
                            userType={user.userType}
                            onClick={(label: string) =>
                              analytics?.clickNavItem(label)
                            }
                            setNextQueryParam={setNextQueryParam}
                          />
                        )}
                      </section>
                    </section>
                  </section>
                  {topBarAddon}
                </section>
              ) : null}

              {!hideMobileNavbar &&
                (useV2TagClassification ? (
                  <MobileNavbarV2
                    features={features}
                    menuItems={Object.keys(updateNavigationMenu)}
                    onClickIntelligence={toggleMobileNavbar}
                  />
                ) : (
                  <PlatformisationMobileNavbar
                    onClickIntelligence={toggleMobileNavbar}
                  />
                ))}
            </nav>
          ) : null}

          <main
            className={"h-full overflow-auto no-scrollbar"}
            onScroll={onScroll}
          >
            {children}
          </main>
        </section>
      </div>

      {showIntelligenceBannerOnPath(
        router.asPath,
        !!features?.platformisation
      ) && showIntelligenceSolutionsBanner ? (
        <IntelligenceSolutionsBanner
          onClose={() => {
            localStorage.setItem(HIDE_INTELLIGENCE_SOLUTIONS_BANNER, "true")
            setShowIntelligenceSolutionsBanner(false)
          }}
        />
      ) : null}
    </>
  )
}

export default DefaultLayoutV3
